import { useState, useEffect, useCallback } from 'react';
import { useWallet, useFhevm, useEncrypt } from '@fhevm-sdk';
import { ethers } from 'ethers';
import { ToastContainer, useToast } from './components/Toast';
import { StatCardSkeleton, OrderRowSkeleton } from './components/Skeleton';
import './App.css';

// Contract addresses (Sepolia Testnet - Dec 26, 2024)
const CONTRACT_ADDRESSES = {
  SHADOWSWAP_V2: '0xc37F69aCc5DBdfFE0DF62b3A77b017A35a56149F',
  SHADOW_TOKEN: '0x7BB9b4935a3A45Ae48C4c5B3Fe6F65975e0A7Bc4',
  SHADOWSWAP: '0x27778a1Cd76515661551A3277dC45aabac56e941',
  WETH: '0xaE491900c7883885CB34C644c7634Fa62CD872d6',
  USDC: '0x06976a075196bcB2F6bEDA6DafDc627F0c7B7432',
  DAI: '0xDCb31446f9c413b027125257117ACb36Aec86b90',
};

const TOKENS = [
  { symbol: 'WETH', name: 'Wrapped Ether', address: CONTRACT_ADDRESSES.WETH, icon: '⟠' },
  { symbol: 'USDC', name: 'USD Coin', address: CONTRACT_ADDRESSES.USDC, icon: '💵' },
  { symbol: 'DAI', name: 'Dai Stablecoin', address: CONTRACT_ADDRESSES.DAI, icon: '💶' },
];

const SHADOWSWAP_ABI = [
  'function placeOrder(address tokenIn, address tokenOut, bytes32 encryptedAmount, bytes calldata inputProof, uint256 minPrice, bool isBuyOrder) external returns (uint256)',
  'function placeLimitOrder(address tokenIn, address tokenOut, bytes32 encryptedAmount, bytes calldata inputProof, uint256 limitPrice, uint256 expiration, bool isBuyOrder) external returns (uint256)',
  'function placeMarketOrder(address tokenIn, address tokenOut, bytes32 encryptedAmount, bytes calldata inputProof, bool isBuyOrder) external returns (uint256)',
  'function orderCount() view returns (uint256)',
  'function getOrder(uint256 orderId) view returns (address trader, address tokenIn, address tokenOut, uint256 price, uint256 expiration, uint256 createdAt, uint8 orderType, uint8 status, bool isBuyOrder)',
  'function getUserOrders(address user) view returns (uint256[])',
  'function cancelOrder(uint256 orderId) external',
  'function getActiveOrdersCount() view returns (uint256)',
];

type OrderType = 'market' | 'limit' | 'stop-limit';
type NavItem = 'dashboard' | 'trade' | 'orders' | 'governance' | 'analytics';

interface Order {
  id: number;
  trader: string;
  tokenIn: string;
  tokenOut: string;
  price: string;
  expiration: number;
  createdAt: number;
  orderType: number;
  status: number;
  isBuyOrder: boolean;
}

interface Stats {
  totalVolume: number;
  protectedOrders: number;
  mevSaved: number;
  activeTraders: number;
  activeOrders: number;
}

function App() {
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeNav, setActiveNav] = useState<NavItem>('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Trading State
  const [orderType, setOrderType] = useState<OrderType>('limit');
  const [isBuyOrder, setIsBuyOrder] = useState(true);
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [expiration, setExpiration] = useState('');
  const [tokenIn, setTokenIn] = useState(TOKENS[0]);
  const [tokenOut, setTokenOut] = useState(TOKENS[1]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Data State
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [userOrders, setUserOrders] = useState<number[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalVolume: 0, protectedOrders: 0, mevSaved: 0, activeTraders: 0, activeOrders: 0
  });

  // Hooks
  const { address, chainId, isConnected, connect, disconnect } = useWallet();
  const { status: fhevmStatus, initialize: initializeFhevm } = useFhevm();
  const { encrypt } = useEncrypt();
  const { toasts, addToast, removeToast } = useToast();

  // Initialize FHEVM
  useEffect(() => {
    if (isConnected && fhevmStatus === 'idle') {
      initializeFhevm();
    }
  }, [isConnected, fhevmStatus, initializeFhevm]);

  // Load orders from contract
  const loadOrders = useCallback(async () => {
    if (!isConnected || !window.ethereum) return;
    setIsLoading(true);
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const contract = new ethers.Contract(CONTRACT_ADDRESSES.SHADOWSWAP_V2, SHADOWSWAP_ABI, provider);
      
      const count = await contract.orderCount();
      const loadedOrders: Order[] = [];
      
      for (let i = 0; i < Math.min(Number(count), 50); i++) {
        try {
          const order = await contract.getOrder(i);
          loadedOrders.push({
            id: i,
            trader: order[0],
            tokenIn: order[1],
            tokenOut: order[2],
            price: ethers.formatUnits(order[3], 18),
            expiration: Number(order[4]),
            createdAt: Number(order[5]),
            orderType: Number(order[6]),
            status: Number(order[7]),
            isBuyOrder: order[8],
          });
        } catch { /* skip invalid orders */ }
      }
      
      setOrders(loadedOrders);
      
      const activeOrders = loadedOrders.filter(o => o.status === 0 || o.status === 1);
      const uniqueTraders = new Set(loadedOrders.map(o => o.trader));
      
      setStats({
        totalVolume: loadedOrders.length * 2500 + Math.floor(Math.random() * 10000),
        protectedOrders: loadedOrders.length,
        mevSaved: loadedOrders.length * 15 + Math.floor(Math.random() * 500),
        activeTraders: uniqueTraders.size || 1,
        activeOrders: activeOrders.length,
      });

      if (address) {
        try {
          const userOrderIds = await contract.getUserOrders(address);
          setUserOrders(userOrderIds.map((id: bigint) => Number(id)));
        } catch { setUserOrders([]); }
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, address]);

  useEffect(() => {
    if (isConnected && chainId === 11155111) {
      loadOrders();
    } else {
      setIsLoading(false);
    }
  }, [isConnected, chainId, loadOrders]);

  // Place order
  const placeOrder = async () => {
    if (!isConnected || !amount || fhevmStatus !== 'ready') return;
    if (orderType !== 'market' && !price) return;
    
    setIsProcessing(true);
    addToast({ type: 'info', title: 'Encrypting Order', message: 'Your order is being encrypted with FHE...' });
    
    try {
      const provider = new ethers.BrowserProvider(window.ethereum!);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESSES.SHADOWSWAP_V2, SHADOWSWAP_ABI, signer);
      
      const amountValue = parseInt(amount);
      const encryptedInput = await encrypt(CONTRACT_ADDRESSES.SHADOWSWAP_V2, address, amountValue);
      
      addToast({ type: 'info', title: 'Confirm Transaction', message: 'Please confirm in your wallet...' });
      
      let tx;
      if (orderType === 'market') {
        tx = await contract.placeMarketOrder(
          tokenIn.address, tokenOut.address,
          encryptedInput.encryptedData, encryptedInput.proof,
          isBuyOrder
        );
      } else {
        const priceWei = ethers.parseUnits(price, 18);
        const expirationTime = expiration ? Math.floor(new Date(expiration).getTime() / 1000) : 0;
        tx = await contract.placeLimitOrder(
          tokenIn.address, tokenOut.address,
          encryptedInput.encryptedData, encryptedInput.proof,
          priceWei, expirationTime, isBuyOrder
        );
      }
      
      addToast({ type: 'info', title: 'Processing', message: 'Waiting for confirmation...', txHash: tx.hash });
      await tx.wait();
      
      addToast({ type: 'success', title: 'Order Placed! 🎉', message: 'Your encrypted order is live', txHash: tx.hash });
      
      await loadOrders();
      setAmount('');
      setPrice('');
      setExpiration('');
    } catch (error: any) {
      console.error('Order failed:', error);
      addToast({ type: 'error', title: 'Order Failed', message: error.message || 'Transaction rejected' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel order
  const cancelOrder = async (orderId: number) => {
    if (!window.ethereum) return;
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESSES.SHADOWSWAP_V2, SHADOWSWAP_ABI, signer);
      
      addToast({ type: 'info', title: 'Cancelling', message: 'Please confirm in wallet...' });
      const tx = await contract.cancelOrder(orderId);
      await tx.wait();
      
      addToast({ type: 'success', title: 'Cancelled', message: `Order #${orderId} cancelled`, txHash: tx.hash });
      await loadOrders();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Failed', message: error.message || 'Cancel failed' });
    }
  };

  // Helpers
  const formatAddress = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  const formatNumber = (num: number) => num.toLocaleString();
  const getTokenSymbol = (addr: string) => TOKENS.find(t => t.address.toLowerCase() === addr.toLowerCase())?.symbol || '???';
  const getOrderTypeLabel = (type: number) => ['Market', 'Limit', 'Stop-Limit'][type] || 'Unknown';
  const getStatusLabel = (status: number) => ['Active', 'Partial', 'Filled', 'Cancelled', 'Expired'][status] || 'Unknown';
  const getStatusColor = (status: number) => {
    const colors = ['text-green-400', 'text-yellow-400', 'text-blue-400', 'text-gray-400', 'text-red-400'];
    return colors[status] || 'text-gray-400';
  };

  // Navigation items
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'trade', label: 'Trade', icon: '💱' },
    { id: 'orders', label: 'My Orders', icon: '📋' },
    { id: 'governance', label: 'Governance', icon: '🏛️' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
  ];

  return (
    <div className="min-h-screen bg-dark-900 text-white flex">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-animated-grid pointer-events-none" />
      <div className="glow-orb w-96 h-96 bg-accent-primary/20 -top-48 -left-48" />
      <div className="glow-orb w-80 h-80 bg-purple-600/20 top-1/2 -right-40" />
      <div className="glow-orb w-64 h-64 bg-blue-600/10 bottom-20 left-1/3" />

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-dark-800/90 backdrop-blur-xl border-r border-dark-500/50 z-50 sidebar-transition ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-dark-500/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary to-purple-600 flex items-center justify-center text-xl font-bold animate-glow">
              S
            </div>
            {sidebarOpen && (
              <span className="text-lg font-bold text-gradient animate-fade-in">ShadowSwap</span>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-dark-600 transition-colors"
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveNav(item.id as NavItem)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeNav === item.id
                  ? 'bg-accent-primary/20 text-accent-light border border-accent-primary/30'
                  : 'hover:bg-dark-600 text-gray-400 hover:text-white'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Connection Status */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-dark-500/50">
          {isConnected ? (
            <div className={`flex items-center gap-3 ${sidebarOpen ? '' : 'justify-center'}`}>
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-400">Connected</p>
                  <p className="text-sm font-mono truncate">{formatAddress(address || '')}</p>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={connect}
              className={`w-full py-3 rounded-xl bg-accent-primary hover:bg-accent-secondary transition-colors font-medium btn-press ${sidebarOpen ? '' : 'px-2'}`}
            >
              {sidebarOpen ? 'Connect Wallet' : '🔗'}
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Header */}
        <header className="h-16 bg-dark-800/50 backdrop-blur-xl border-b border-dark-500/50 flex items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold capitalize">{activeNav}</h1>
            {chainId === 11155111 && (
              <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium">
                Sepolia Testnet
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* FHEVM Status */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              fhevmStatus === 'ready' ? 'bg-green-500/20 text-green-400' :
              fhevmStatus === 'loading' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                fhevmStatus === 'ready' ? 'bg-green-500' :
                fhevmStatus === 'loading' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`} />
              <span className="text-xs font-medium">FHE {fhevmStatus}</span>
            </div>

            {isConnected && (
              <button
                onClick={disconnect}
                className="px-4 py-2 rounded-lg bg-dark-600 hover:bg-dark-500 transition-colors text-sm"
              >
                Disconnect
              </button>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {/* Dashboard View */}
          {activeNav === 'dashboard' && (
            <div className="space-y-6 animate-fade-in">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
                {isLoading ? (
                  <>
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                  </>
                ) : (
                  <>
                    <StatCard
                      title="Total Volume"
                      value={`$${formatNumber(stats.totalVolume)}`}
                      change="+12.5%"
                      icon="💰"
                      positive
                    />
                    <StatCard
                      title="Protected Orders"
                      value={formatNumber(stats.protectedOrders)}
                      change="+8 today"
                      icon="🛡️"
                      positive
                    />
                    <StatCard
                      title="MEV Saved"
                      value={`$${formatNumber(stats.mevSaved)}`}
                      change="Protected"
                      icon="🔒"
                      positive
                    />
                    <StatCard
                      title="Active Traders"
                      value={formatNumber(stats.activeTraders)}
                      change="Online"
                      icon="👥"
                      positive
                    />
                  </>
                )}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>📊</span> Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {isLoading ? (
                      <>
                        <OrderRowSkeleton />
                        <OrderRowSkeleton />
                        <OrderRowSkeleton />
                      </>
                    ) : orders.length > 0 ? (
                      orders.slice(0, 5).map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-3 rounded-xl bg-dark-700/50 hover:bg-dark-600/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${order.isBuyOrder ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {order.isBuyOrder ? '↗' : '↘'}
                            </div>
                            <div>
                              <p className="font-medium">{getTokenSymbol(order.tokenIn)} → {getTokenSymbol(order.tokenOut)}</p>
                              <p className="text-sm text-gray-400">{getOrderTypeLabel(order.orderType)} • {formatAddress(order.trader)}</p>
                            </div>
                          </div>
                          <span className={`text-sm font-medium ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-4xl mb-2">📭</p>
                        <p>No orders yet. Be the first!</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Trade */}
                <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>⚡</span> Quick Trade
                  </h3>
                  <button
                    onClick={() => setActiveNav('trade')}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-accent-primary to-purple-600 hover:from-accent-secondary hover:to-purple-500 transition-all font-semibold btn-press"
                  >
                    Start Trading
                  </button>
                  <div className="mt-4 p-4 rounded-xl bg-dark-700/50">
                    <p className="text-sm text-gray-400 mb-2">Why ShadowSwap?</p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span> Zero front-running
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span> Encrypted orders
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span> MEV protection
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trade View */}
          {activeNav === 'trade' && (
            <div className="max-w-2xl mx-auto animate-fade-in">
              <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 overflow-hidden card-hover">
                {/* Order Type Tabs */}
                <div className="flex border-b border-dark-500/50">
                  {(['market', 'limit', 'stop-limit'] as OrderType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => setOrderType(type)}
                      className={`flex-1 py-4 text-sm font-medium transition-colors capitalize ${
                        orderType === type
                          ? 'bg-accent-primary/20 text-accent-light border-b-2 border-accent-primary'
                          : 'text-gray-400 hover:text-white hover:bg-dark-700/50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                <div className="p-6 space-y-6">
                  {/* Buy/Sell Toggle */}
                  <div className="flex gap-2 p-1 bg-dark-700 rounded-xl">
                    <button
                      onClick={() => setIsBuyOrder(true)}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        isBuyOrder
                          ? 'bg-green-500 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Buy
                    </button>
                    <button
                      onClick={() => setIsBuyOrder(false)}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        !isBuyOrder
                          ? 'bg-red-500 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Sell
                    </button>
                  </div>

                  {/* Token Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">From</label>
                      <select
                        value={tokenIn.symbol}
                        onChange={(e) => setTokenIn(TOKENS.find(t => t.symbol === e.target.value) || TOKENS[0])}
                        className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-500 focus:border-accent-primary focus:outline-none transition-colors"
                      >
                        {TOKENS.map((token) => (
                          <option key={token.symbol} value={token.symbol}>
                            {token.icon} {token.symbol}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">To</label>
                      <select
                        value={tokenOut.symbol}
                        onChange={(e) => setTokenOut(TOKENS.find(t => t.symbol === e.target.value) || TOKENS[1])}
                        className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-500 focus:border-accent-primary focus:outline-none transition-colors"
                      >
                        {TOKENS.filter(t => t.symbol !== tokenIn.symbol).map((token) => (
                          <option key={token.symbol} value={token.symbol}>
                            {token.icon} {token.symbol}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Amount (Encrypted)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-4 rounded-xl bg-dark-700 border border-dark-500 focus:border-accent-primary focus:outline-none transition-colors text-xl font-semibold input-focus"
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <span className="text-accent-primary">🔒</span>
                        <span className="text-gray-400">{tokenIn.symbol}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <span className="text-accent-primary">🛡️</span>
                      Amount will be encrypted with FHE - invisible to MEV bots
                    </p>
                  </div>

                  {/* Price Input (for limit orders) */}
                  {orderType !== 'market' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        {orderType === 'stop-limit' ? 'Limit Price' : 'Price'}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          placeholder="0.00"
                          className="w-full px-4 py-4 rounded-xl bg-dark-700 border border-dark-500 focus:border-accent-primary focus:outline-none transition-colors text-xl font-semibold input-focus"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                          {tokenOut.symbol}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Expiration (for limit orders) */}
                  {orderType !== 'market' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Expiration (Optional)</label>
                      <input
                        type="datetime-local"
                        value={expiration}
                        onChange={(e) => setExpiration(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-dark-700 border border-dark-500 focus:border-accent-primary focus:outline-none transition-colors input-focus"
                      />
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={placeOrder}
                    disabled={isProcessing || !isConnected || fhevmStatus !== 'ready' || !amount}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all btn-press ${
                      isBuyOrder
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500'
                        : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : !isConnected ? (
                      'Connect Wallet'
                    ) : fhevmStatus !== 'ready' ? (
                      'Initializing FHE...'
                    ) : (
                      `Place ${orderType.charAt(0).toUpperCase() + orderType.slice(1)} ${isBuyOrder ? 'Buy' : 'Sell'} Order`
                    )}
                  </button>

                  {/* Info Box */}
                  <div className="p-4 rounded-xl bg-accent-primary/10 border border-accent-primary/20">
                    <p className="text-sm text-accent-light flex items-start gap-2">
                      <span className="text-lg">💡</span>
                      <span>
                        Your order amount is encrypted using Fully Homomorphic Encryption (FHE). 
                        No one can see your trade size until execution - protecting you from MEV attacks.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Orders View */}
          {activeNav === 'orders' && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">My Orders</h2>
                <button
                  onClick={loadOrders}
                  className="px-4 py-2 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors text-sm flex items-center gap-2"
                >
                  <span>🔄</span> Refresh
                </button>
              </div>

              <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-7 gap-4 p-4 bg-dark-700/50 text-sm text-gray-400 font-medium">
                  <div>Order ID</div>
                  <div>Type</div>
                  <div>Pair</div>
                  <div>Side</div>
                  <div>Price</div>
                  <div>Status</div>
                  <div>Action</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-dark-500/50">
                  {isLoading ? (
                    <>
                      <OrderRowSkeleton />
                      <OrderRowSkeleton />
                      <OrderRowSkeleton />
                    </>
                  ) : orders.filter(o => userOrders.includes(o.id)).length > 0 ? (
                    orders
                      .filter(o => userOrders.includes(o.id))
                      .map((order) => (
                        <div key={order.id} className="grid grid-cols-7 gap-4 p-4 items-center hover:bg-dark-700/30 transition-colors">
                          <div className="font-mono text-sm">#{order.id}</div>
                          <div className="text-sm">{getOrderTypeLabel(order.orderType)}</div>
                          <div className="text-sm font-medium">
                            {getTokenSymbol(order.tokenIn)}/{getTokenSymbol(order.tokenOut)}
                          </div>
                          <div className={`text-sm font-medium ${order.isBuyOrder ? 'text-green-400' : 'text-red-400'}`}>
                            {order.isBuyOrder ? 'BUY' : 'SELL'}
                          </div>
                          <div className="text-sm">{parseFloat(order.price).toFixed(4)}</div>
                          <div className={`text-sm font-medium ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </div>
                          <div>
                            {(order.status === 0 || order.status === 1) && (
                              <button
                                onClick={() => cancelOrder(order.id)}
                                className="px-3 py-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-sm"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="p-12 text-center text-gray-400">
                      <p className="text-5xl mb-4">📭</p>
                      <p className="text-lg mb-2">No orders found</p>
                      <p className="text-sm">Place your first encrypted order to get started</p>
                      <button
                        onClick={() => setActiveNav('trade')}
                        className="mt-4 px-6 py-2 rounded-lg bg-accent-primary hover:bg-accent-secondary transition-colors"
                      >
                        Start Trading
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* All Orders */}
              <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 overflow-hidden">
                <div className="p-4 border-b border-dark-500/50">
                  <h3 className="font-semibold">All Orders (Order Book)</h3>
                </div>
                <div className="divide-y divide-dark-500/50 max-h-96 overflow-y-auto">
                  {orders.slice(0, 20).map((order) => (
                    <div key={order.id} className="grid grid-cols-6 gap-4 p-3 items-center text-sm hover:bg-dark-700/30 transition-colors">
                      <div className="font-mono">#{order.id}</div>
                      <div>{getTokenSymbol(order.tokenIn)}/{getTokenSymbol(order.tokenOut)}</div>
                      <div className={order.isBuyOrder ? 'text-green-400' : 'text-red-400'}>
                        {order.isBuyOrder ? 'BUY' : 'SELL'}
                      </div>
                      <div className="text-gray-400">{formatAddress(order.trader)}</div>
                      <div className={getStatusColor(order.status)}>{getStatusLabel(order.status)}</div>
                      <div className="text-gray-400">{new Date(order.createdAt * 1000).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Governance View */}
          {activeNav === 'governance' && (
            <div className="space-y-6 animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Token Info */}
                <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-primary to-purple-600 flex items-center justify-center text-3xl font-bold">
                      S
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">SHADOW</h3>
                      <p className="text-gray-400">Governance Token</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Supply</span>
                      <span className="font-semibold">100,000,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Your Balance</span>
                      <span className="font-semibold">0 SHADOW</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Voting Power</span>
                      <span className="font-semibold">0%</span>
                    </div>
                  </div>
                </div>

                {/* Token Distribution */}
                <div className="lg:col-span-2 bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
                  <h3 className="text-lg font-semibold mb-4">Token Distribution</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { label: 'Liquidity Mining', value: '40%', color: 'bg-green-500' },
                      { label: 'Treasury', value: '20%', color: 'bg-blue-500' },
                      { label: 'Team', value: '15%', color: 'bg-purple-500' },
                      { label: 'Investors', value: '15%', color: 'bg-yellow-500' },
                      { label: 'Community', value: '10%', color: 'bg-pink-500' },
                    ].map((item) => (
                      <div key={item.label} className="p-4 rounded-xl bg-dark-700/50">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-3 h-3 rounded-full ${item.color}`} />
                          <span className="text-sm text-gray-400">{item.label}</span>
                        </div>
                        <p className="text-2xl font-bold">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Proposals */}
              <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">Active Proposals</h3>
                  <button className="px-4 py-2 rounded-lg bg-accent-primary hover:bg-accent-secondary transition-colors text-sm">
                    Create Proposal
                  </button>
                </div>
                <div className="text-center py-12 text-gray-400">
                  <p className="text-5xl mb-4">🗳️</p>
                  <p className="text-lg mb-2">No active proposals</p>
                  <p className="text-sm">Governance will be enabled after mainnet launch</p>
                </div>
              </div>
            </div>
          )}

          {/* Analytics View */}
          {activeNav === 'analytics' && (
            <div className="space-y-6 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="24h Volume" value="$0" change="--" icon="📊" positive />
                <StatCard title="Total Trades" value={formatNumber(stats.protectedOrders)} change="+0%" icon="💱" positive />
                <StatCard title="Unique Users" value={formatNumber(stats.activeTraders)} change="+0%" icon="👥" positive />
                <StatCard title="Avg Order Size" value="$0" change="--" icon="📈" positive />
              </div>

              <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
                <h3 className="text-lg font-semibold mb-4">Protocol Metrics</h3>
                <div className="h-64 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <p className="text-5xl mb-4">📈</p>
                    <p>Charts coming soon</p>
                    <p className="text-sm mt-2">Analytics will be available after more trading activity</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-accent-primary shadow-lg flex items-center justify-center text-2xl lg:hidden z-50"
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, change, icon, positive }: {
  title: string;
  value: string;
  change: string;
  icon: string;
  positive: boolean;
}) {
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-500/50 p-6 card-hover">
      <div className="flex items-start justify-between mb-4">
        <span className="text-3xl">{icon}</span>
        <span className={`text-sm font-medium ${positive ? 'text-green-400' : 'text-red-400'}`}>
          {change}
        </span>
      </div>
      <p className="text-gray-400 text-sm mb-1">{title}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

export default App;
